import React from 'react';
import classnames from 'classnames';
import { Checkbox } from '@mui/material';
import { ReactComponent as PlusIcon } from './assets/plus.svg';
import DebouncedText from '../../../elements/editors/mui/DebouncedText';

import { components } from 'react-select';
import { Close as CloseIcon } from '@mui/icons-material';

import { filter, find, findIndex, get, has, includes, isEqual, isNil, map } from 'lodash';
import { FieldBuilderRedesigned as FieldBuilder } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import {
  baseSelectStyles,
  getCurrentSelectedOption,
  getNewSelectionValue,
  SelectInput,
  SelectOption,
} from '../../../elements/rselect/ReactSelectUtils';
import SortedSelect from '../../../elements/rselect/SortedSelect';
import { TableColumns } from '../../../data_sources/add_ds_form/database/TableList';
import { convertToValidDBColumnName } from 'js/lib/ValidationUtils';
import UIUtils from '../../../../lib/UIUtils';
import DestinationPayloadContainer from '../DestinationPayloadContainer';
import { Button } from 'new/elements/Button';
import { Stack } from 'new/elements/Stack';
import flowTypes from 'new/components/flows/types/flowTypes';

const styles = {
  disabledLine: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.3)',
  },
  tableNameField: {
    wrapperDiv: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: 16,
    },
  },
  upsertNullsField: {
    wrapperDiv: {
      width: '50%',
      paddingBottom: 10,
    },
    rootDiv: {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      marginLeft: 20,
    },
    checkboxLabelDiv: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    checkboxLabelTitle: {
      fontSize: 10,
      fontWeight: 500,
    },
    checkboxSubtitleDiv: {
      fontSize: 10,
    },
  },
};

const SchemaAttributeOption = (props) => {
  const { data } = props;
  if (isNil(data)) {
    return (
      <components.Option {...props}>
        <div className="attr-option" data-testid={`option-null`}>
          <div className="attr-option-name">{''}</div>
          <div className="attr-option-sub">{''}</div>
        </div>
      </components.Option>
    );
  }
  const { name, description, type, value, __isNew__ } = data;

  if (__isNew__) {
    return (
      <components.Option {...props}>
        <div className="attr-option" data-testid={`option-new`}>
          <div className="attr-option-name">Create "{value || ''}"</div>
        </div>
      </components.Option>
    );
  }

  return (
    <components.Option {...props}>
      <div className="attr-option">
        <div className="attr-option-name" data-testid={`option-${name}`}>
          {name || ''}
        </div>
        <div className="attr-option-sub">{description || type || ''}</div>
      </div>
    </components.Option>
  );
};

const SchemaAttributeValue = (props) => {
  const { data } = props;

  if (isNil(data)) {
    return (
      <components.SingleValue {...props}>
        <div className="attr-option">
          <div className="attr-option-name" data-testid={`attribute-value-null`}>
            {''}
          </div>
          <div className="attr-option-sub attr-option-sub-small">{''}</div>
        </div>
      </components.SingleValue>
    );
  }
  const { name, value, description, type } = data;

  return (
    <components.SingleValue {...props}>
      <div className="attr-option">
        <div
          className="attr-option-name"
          data-testid={`attribute-value${name || value ? '-' + (name || value) : ''}`}
        >
          {name || value || ''}
        </div>
        <div className="attr-option-sub attr-option-sub-small">
          {description || type || (value ? 'custom' : '')}
        </div>
      </div>
    </components.SingleValue>
  );
};

const SchemaAttributePlaceholder = (props) => {
  const styles = { fontSize: 10 };
  return (
    <components.Placeholder {...props}>
      <div style={styles}>
        <span>Select or Create an Attribute</span>
      </div>
    </components.Placeholder>
  );
};

class DataSchemaAttributeSelector extends React.Component {
  _onAttrSelected = (selectedOption) => {
    let selectedAttr = getNewSelectionValue(selectedOption, true, '');
    this.props.onChange(selectedAttr);
  };

  render() {
    let { dataSchema, value } = this.props;
    let selectedOption = getCurrentSelectedOption(dataSchema, value, null, null, true);

    return (
      <SortedSelect
        isCreatable
        isClearable={true}
        backspaceRemovesValue={false}
        options={dataSchema}
        styles={{
          ...baseSelectStyles,
          valueContainer: (base) => ({
            ...base,
            height: 32,
            marginTop: -4,
          }),
        }}
        components={{
          Option: SchemaAttributeOption,
          SingleValue: SchemaAttributeValue,
          Placeholder: SchemaAttributePlaceholder,
          Input: SelectInput,
        }}
        noOptionsMessagefunction="No attibutes"
        name="selected-destination"
        data-testid={this.props['data-testid']}
        isSearchable={true}
        value={selectedOption}
        onChange={this._onAttrSelected}
      />
    );
  }
}

class TableEditor extends React.Component {
  render() {
    const {
      addedDataSink,
      currentOrg,
      sinkConfigFields,
      table,
      isNew,
      editSelectedTable,
      tableNotFound,
      selectedConnection,
      connectionMeta: { allowSinkAutoMapping },
      sinkConfig,
      onSinkConfigChanged,
    } = this.props;
    const isUpsertMode = table['insert.mode'] === 'UPSERT';
    const isAutoMode = !!(
      allowSinkAutoMapping &&
      !isUpsertMode &&
      sinkConfig.mapping &&
      sinkConfig.mapping.mode === 'auto'
    );
    const hcScaleFactorField = find(sinkConfigFields[selectedConnection], {
      name: 'hc_scale_factor',
    });
    const hcScaleFactor =
      (sinkConfig && sinkConfig['hc_scale_factor']) || hcScaleFactorField?.default;
    const hcScaleFactorOptions = UIUtils.disableInvalidHCOptions(
      hcScaleFactorField.options,
      addedDataSink && addedDataSink.sink_config && addedDataSink.sink_config['hc_scale_factor'],
    );

    const fireBoltTableTypeField =
      selectedConnection === 'firebolt'
        ? find(sinkConfigFields[selectedConnection], {
            name: 'firebolt.table.type',
          })
        : null;

    return (
      <div className="table-editor-wrapper">
        {isNew && !isNil(fireBoltTableTypeField) && (
          <div>
            <div className="table-field-wrapper">
              <FieldBuilder
                field={fireBoltTableTypeField}
                currentFieldValue={table['firebolt.table.type'] || fireBoltTableTypeField.default}
                isRequired={true}
                showRequiredIndicator={true}
                id="firebolt.table.type"
                fieldLabelClassName="adv-settings-sink-firebolt.table.type adv-settings-database-firebolt.table.type"
                onChange={editSelectedTable}
              />
            </div>
          </div>
        )}
        {isNew && (
          <div className="table-field-wrapper" style={styles.tableNameField.wrapperDiv}>
            <label htmlFor={'tableName'}>Table Name *</label>
            <DebouncedText
              id="TableName"
              data-testid="TableName"
              value={table.name}
              debounceTimeMs={200}
              onChange={(value) => {
                editSelectedTable('name', convertToValidDBColumnName(value, selectedConnection));
              }}
            />
          </div>
        )}
        {tableNotFound && (
          <div className="table-not-found-warning">
            {' '}
            We couldn't retrieve the table info from database, you can continue and we attempt to
            create a new table or you can go back and choose a new table.
          </div>
        )}
        {allowSinkAutoMapping && isAutoMode && this._renderAutoModeContent()}
        {UIUtils.isHCEnabled(currentOrg.id, selectedConnection, false) && (
          <div>
            <div className="table-field-wrapper" data-testid={`select_${hcScaleFactorField.name}`}>
              <label>{hcScaleFactorField.display_name}</label>
              <SortedSelect
                value={getCurrentSelectedOption(hcScaleFactorOptions, hcScaleFactor)}
                components={{
                  Input: SelectInput,
                  Option: SelectOption,
                }}
                options={hcScaleFactorOptions}
                isClearable={false}
                backspaceRemovesValue={false}
                isSearchable={true}
                styles={baseSelectStyles}
                onChange={(newValue) => {
                  const value = getNewSelectionValue(newValue, true);
                  onSinkConfigChanged({
                    ...sinkConfig,
                    [hcScaleFactorField.name]: value,
                  });
                }}
              />
            </div>
          </div>
        )}
        {!isAutoMode && this._renderManualModeContent()}
      </div>
    );
  }

  _renderAutoModeContent = () => {
    const { isNew, table, sinkConfig } = this.props;

    return (
      <div className="auto-mode-content">
        <DestinationPayloadContainer
          heading="Sample Records for Database"
          description="With automatic mapping enabled, column data types will be inferred from record values and nesting of attributes will be preserved. These records below are some of the samples that will be used for automatic mapping. You can change the table structure by updating the output attributes of this Nexset."
          panelHeading="Sample Record"
          disableTabs
        />
        {!isNew && sinkConfig && sinkConfig.table && table && (
          <section className="api-response-holder table-schema-holder">
            <header>
              <h1>Schema of table {sinkConfig.table}</h1>
            </header>
            <div className="api-response-holder-content">
              <div className="description">
                You have selected the table <b>{sinkConfig.table}</b>. Please note that records
                which cannot be mapped to this table's schema will be treated as errors.
              </div>
              <TableColumns columns={table.columns} />
            </div>
          </section>
        )}
      </div>
    );
  };

  _renderManualModeContent = () => {
    const {
      flowType,
      dataTypes,
      table,
      dataSchema,
      isNew,
      addSelectedTableColum,
      editSelectedTableColumns,
      removeSelectedTableColum,
      clearColumnAttribute,
      sinkConfig,
      sinkConfigFields,
      selectedConnection,
      onSinkConfigChanged,
    } = this.props;

    let primaryKeyField =
      sinkConfigFields[selectedConnection].filter((field) => field.name === 'primary.key')[0] ||
      null;

    let partioningField =
      sinkConfigFields[selectedConnection].filter(
        (field) => field.name === 'partitioning.column',
      )[0] || null;

    let clusteringField =
      sinkConfigFields[selectedConnection].filter(
        (field) => field.name === 'clustering.columns',
      )[0] || null;

    let cleanroomAccountNameField =
      sinkConfigFields[selectedConnection].filter(
        (field) => field.name === 'cleanroom.consumer.accountname',
      )[0] || null;

    let cleanroomJoinField =
      sinkConfigFields[selectedConnection].filter(
        (field) => field.name === 'cleanroom.join.keys',
      )[0] || null;

    let cleanroomDimensionField =
      sinkConfigFields[selectedConnection].filter(
        (field) => field.name === 'cleanroom.dimensions',
      )[0] || null;

    const dataTypeOptions = map(dataTypes, (dt) => ({
      label: dt,
      value: dt,
    }));
    const isUpsertMode = table['insert.mode'] === 'UPSERT';
    const hasDefaultValue = findIndex(table.columns, (c) => has(c, 'defaultValue')) > -1;

    let shouldRenderPrimaryKeyField = primaryKeyField
      ? UIUtils.shouldRenderField(primaryKeyField, sinkConfig)
      : false;

    let shouldRenderPartitioningField = partioningField
      ? UIUtils.shouldRenderField(partioningField, sinkConfig)
      : false;

    let shouldRenderClusteringField = clusteringField
      ? UIUtils.shouldRenderField(clusteringField, sinkConfig)
      : false;

    let shouldRenderCleanroomAccountField = cleanroomAccountNameField
      ? UIUtils.shouldRenderField(cleanroomAccountNameField, sinkConfig)
      : false;

    let shouldRenderCleanroomJoinField = cleanroomJoinField
      ? UIUtils.shouldRenderField(cleanroomJoinField, sinkConfig)
      : false;

    let shouldRenderCleanroomDimensionField = cleanroomDimensionField
      ? UIUtils.shouldRenderField(cleanroomDimensionField, sinkConfig)
      : false;

    return (
      <div>
        {shouldRenderCleanroomAccountField && (
          <div className="table-field-wrapper" style={styles.tableNameField.wrapperDiv}>
            <label htmlFor={'tableName'}>Account Name for Consumer *</label>
            <DebouncedText
              id="cleanroom.consumer.accountname"
              value={get(sinkConfig, 'cleanroom.consumer.accountname', '')}
              debounceTimeMs={200}
              onChange={(value) => {
                onSinkConfigChanged({
                  ...sinkConfig,
                  ['cleanroom.consumer.accountname']: value,
                });
              }}
            />
          </div>
        )}

        <table className="table-editor">
          <thead>
            <tr>
              <th>Linked Draft Attribute</th>
              <th>Column</th>
              <th>Type</th>
              {shouldRenderPrimaryKeyField && <th className="small">Primary</th>}
              {shouldRenderPartitioningField && <th className="small">Partitioning Column</th>}
              {shouldRenderClusteringField && <th className="small">Clustering Columns</th>}
              {shouldRenderCleanroomJoinField && <th className="small">Cleanroom Join Keys</th>}
              {shouldRenderCleanroomDimensionField && (
                <th className="small">Cleanroom Dimension Columns</th>
              )}
              {hasDefaultValue && <th className="big">Default</th>}
              {flowType !== flowTypes.replication && <th className="small" />}
            </tr>
          </thead>
          <tbody>
            {map(table.columns, (column, ix) => (
              <ColumnRow
                key={'row_' + ix}
                ix={ix}
                flowType={flowType}
                column={column}
                isNew={isNew}
                dataSchema={dataSchema}
                dataTypeOptions={dataTypeOptions}
                isUpsertMode={isUpsertMode}
                sinkConfig={sinkConfig}
                hasDefaultValue={hasDefaultValue}
                removeSelectedTableColum={removeSelectedTableColum}
                clearColumnAttribute={clearColumnAttribute}
                editSelectedTableColumns={editSelectedTableColumns}
                shouldRenderPrimaryKeyField={shouldRenderPrimaryKeyField}
                shouldRenderPartitioningField={shouldRenderPartitioningField}
                shouldRenderClusteringField={shouldRenderClusteringField}
                shouldRenderCleanroomJoinField={shouldRenderCleanroomJoinField}
                shouldRenderCleanroomDimensionField={shouldRenderCleanroomDimensionField}
              />
            ))}
          </tbody>
        </table>
        {isNew && (
          <Stack paddingTop="16px" alignItems="flex-start">
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PlusIcon />}
              onClick={() => {
                addSelectedTableColum();
              }}
            >
              New Column
            </Button>
          </Stack>
        )}
      </div>
    );
  };
}

class ColumnRow extends React.Component {
  shouldComponentUpdate(nextProps) {
    // render if column changed or primary key check box changed
    return (
      (this.props.column !== nextProps.column && !isEqual(this.props.column, nextProps.column)) ||
      this.props.isUpsertMode !== nextProps.isUpsertMode ||
      (this.props.sinkConfig['primary.key'] !== nextProps.sinkConfig['primary.key'] &&
        (includes(nextProps.sinkConfig['primary.key'], nextProps.column.name) ||
          includes(this.props.sinkConfig['primary.key'], this.props.column.name))) ||
      (this.props.sinkConfig['partitioning.column'] !==
        nextProps.sinkConfig['partitioning.column'] &&
        (includes(nextProps.sinkConfig['partitioning.column'], nextProps.column.name) ||
          includes(this.props.sinkConfig['partitioning.column'], this.props.column.name))) ||
      (this.props.sinkConfig['clustering.columns'] !== nextProps.sinkConfig['clustering.columns'] &&
        (includes(nextProps.sinkConfig['clustering.columns'], nextProps.column.name) ||
          includes(this.props.sinkConfig['clustering.columns'], this.props.column.name))) ||
      (this.props.sinkConfig['cleanroom.join.keys'] !==
        nextProps.sinkConfig['cleanroom.join.keys'] &&
        (includes(nextProps.sinkConfig['cleanroom.join.keys'], nextProps.column.name) ||
          includes(this.props.sinkConfig['cleanroom.join.keys'], this.props.column.name))) ||
      (this.props.sinkConfig['cleanroom.dimensions'] !==
        nextProps.sinkConfig['cleanroom.dimensions'] &&
        (includes(nextProps.sinkConfig['cleanroom.dimensions'], nextProps.column.name) ||
          includes(this.props.sinkConfig['cleanroom.dimensions'], this.props.column.name)))
    );
  }

  render() {
    const {
      isNew,
      column,
      ix,
      flowType,
      dataSchema,
      dataTypeOptions,
      isUpsertMode,
      sinkConfig,
      hasDefaultValue,
      removeSelectedTableColum,
      clearColumnAttribute,
      editSelectedTableColumns,
      shouldRenderPrimaryKeyField,
      shouldRenderClusteringField,
      shouldRenderCleanroomJoinField,
      shouldRenderCleanroomDimensionField,
      shouldRenderPartitioningField,
    } = this.props;

    return (
      <tr
        className={classnames({
          'unmapped-db-column': !isNew && !column.attr,
        })}
      >
        <td>
          {flowType === flowTypes.replication ? (
            column.attr || ''
          ) : (
            <DataSchemaAttributeSelector
              index={ix}
              data-testid={`select-dataset-attribute-${ix}`}
              dataSchema={dataSchema}
              value={column.attr || ''}
              onChange={(selected) => {
                let attr = filter(dataSchema, (s) => s.value === selected).shift();
                editSelectedTableColumns(ix, 'attr', selected, attr ? attr.name : null);
              }}
            />
          )}
        </td>
        <td className="db-column-name-cell">
          {isNew && flowType !== flowTypes.replication ? (
            <DebouncedText
              key={`Column_${ix}`}
              value={column.name || ''}
              onChange={(value) => {
                editSelectedTableColumns(ix, 'name', value);
              }}
            />
          ) : (
            column.name || ''
          )}
        </td>
        <td className="db-column-name-cell">
          {isNew && flowType !== flowTypes.replication ? (
            <SortedSelect
              isCreatable={true}
              key={`Type_${ix}`}
              inputId={`Type_${ix}`}
              data-testid={`select-attribute-${ix}`}
              options={dataTypeOptions}
              formatCreateLabel={(value) => value}
              isClearable={false}
              backspaceRemovesValue={false}
              styles={{
                ...baseSelectStyles,
                singleValue: (base) => ({
                  ...base,
                  overflow: 'unset',
                }),
              }}
              components={{
                Input: SelectInput,
                Option: SelectOption,
              }}
              isSearchable={true}
              value={getCurrentSelectedOption(dataTypeOptions, column.type, undefined, null, true)}
              onChange={({ value }) => {
                editSelectedTableColumns(ix, 'type', value);
              }}
            />
          ) : (
            column.type || ''
          )}
        </td>
        {shouldRenderPrimaryKeyField && (
          <td className="small">
            <Checkbox
              key={`Primary_${ix}`}
              id={`Primary_${ix}`}
              className="table-editor-checkbox"
              checked={new RegExp(`\\b${column.name}\\b`).test(sinkConfig['primary.key'])}
              onChange={(ev, value) => {
                editSelectedTableColumns(ix, 'primaryKey', value);
              }}
              disabled={!column.attr}
              data-testid={`primary-key-${ix}`}
            />
          </td>
        )}
        {shouldRenderPartitioningField && (
          <td className="small">
            <Checkbox
              key={`Partioning_column_${ix}`}
              id={`Partioning_column_${ix}`}
              className="table-editor-checkbox"
              checked={new RegExp(`\\b${column.name}\\b`).test(sinkConfig['partitioning.column'])}
              onChange={(ev, value) => {
                editSelectedTableColumns(ix, 'partitioning.column', value);
              }}
              disabled={!column.attr}
            />
          </td>
        )}
        {shouldRenderClusteringField && (
          <td className="small">
            <Checkbox
              key={`Clustering_${ix}`}
              id={`Clustering_${ix}`}
              className="table-editor-checkbox"
              checked={new RegExp(`\\b${column.name}\\b`).test(sinkConfig['clustering.columns'])}
              onChange={(ev, value) => {
                editSelectedTableColumns(ix, 'clustering.columns', value);
              }}
              disabled={!column.attr}
            />
          </td>
        )}
        {shouldRenderCleanroomJoinField && (
          <td className="small">
            <Checkbox
              key={`Cleanroom_Join_${ix}`}
              id={`Cleanroom_Join_${ix}`}
              className="table-editor-checkbox"
              checked={new RegExp(`\\b${column.name}\\b`).test(sinkConfig['cleanroom.join.keys'])}
              onChange={(ev, value) => {
                editSelectedTableColumns(ix, 'cleanroom.join.keys', value);
              }}
              disabled={!column.attr}
            />
          </td>
        )}
        {shouldRenderCleanroomDimensionField && (
          <td className="small">
            <Checkbox
              key={`Cleanroom_Dimension_${ix}`}
              id={`Clearnroom_Dimension_${ix}`}
              className="table-editor-checkbox"
              checked={new RegExp(`\\b${column.name}\\b`).test(sinkConfig['cleanroom.dimensions'])}
              onChange={(ev, value) => {
                editSelectedTableColumns(ix, 'cleanroom.dimensions', value);
              }}
              disabled={!column.attr}
            />
          </td>
        )}
        {hasDefaultValue && (
          <td
            className={classnames('big', {
              'missing-default-value': !has(column, 'defaultValue'),
            })}
          >
            {has(column, 'defaultValue') ? String(column.defaultValue) : '(none)'}
          </td>
        )}
        {flowType !== flowTypes.replication && (
          <td
            className="small"
            onClick={() => {
              if (isNew) {
                removeSelectedTableColum(ix);
              } else if (column.attr) {
                clearColumnAttribute(ix);
              }
            }}
          >
            <span className={'table-editor-delete-column'}>
              {(isNew || column.attr) && <CloseIcon fontSize="large" htmlColor="#41CE8B" />}
            </span>
          </td>
        )}
      </tr>
    );
  }
}

export default TableEditor;
